import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, catchError, map, of } from 'rxjs';

import { environment } from '../../../environments/environment';
import { ApiResponse } from '@app/shared/_models/common';
import { jsonheader } from '@app/shared/constants';
import { EventType, ObjectType } from '@app/shared/constants-server';
import { serverMessage } from '@app/sales/rental-billing/rental-billing.service';
import { RentalCard, RentalProductRow, RentalProductSheet, TaxRate, cardPayload } from './rental-products.model';

export type CardSaveResult = { ok: true; id: number; message: string } | { ok: false; message: string };

// Ce qui accompagne le contrat de location dans l'évènement : rattachements (product_rental_terms_val) appliqués dans la même transaction.
export interface CardLinks {
  link_products?: { product: number; seq?: number }[];
  unlink_products?: number[];
  replaces?: number;
}

@Injectable({ providedIn: 'root' })
export class RentalProductsService {
  private http = inject(HttpClient);

  getRentalProducts(): Observable<RentalProductRow[]> {
    return this.http.get<RentalProductRow[]>(`${environment.server_url}/sales/rental_products`);
  }

  getRentalProduct(id: number): Observable<RentalProductSheet> {
    return this.http.get<RentalProductSheet>(`${environment.server_url}/sales/rental_products/${id}`);
  }

  // Tout le catalogue des contrats de location, pour en rattacher un existant.
  getCards(): Observable<RentalCard[]> {
    return this.http.get<RentalCard[]>(`${environment.server_url}/sales/rental_terms`);
  }

  getTaxes(): Observable<TaxRate[]> {
    return this.http.get<TaxRate[]>(`${environment.server_url}/common/taxes`);
  }

  // id 0 = création (évènement INSERT) ; le serveur refuse un contrat que le moteur bloquerait et rend le motif.
  saveCard(card: RentalCard, links: CardLinks = {}): Observable<CardSaveResult> {
    const isNew = !card.id;
    const envelope = {
      id: 0, typ: isNew ? EventType.INSERT : EventType.UPDATE, userid: 0, objecttyp: ObjectType.RENTAL_TERMS, objectid: card.id || 0,
      data: { ...cardPayload(card), ...links },
    };
    return this.http.post<ApiResponse<{ rental_terms_id?: number }>>(`${environment.server_url}/event`, envelope, { headers: jsonheader }).pipe(
      map((res): CardSaveResult => (res?.success
        ? { ok: true, id: Number(res.data?.rental_terms_id ?? card.id), message: res.msg ?? '' }
        : { ok: false, message: res?.msg || res?.error_msg || 'Erreur serveur' })),
      catchError((e) => of<CardSaveResult>({ ok: false, message: serverMessage(e) })),
    );
  }
}
